import React, { Fragment } from "react"
import { IconBook2, IconCode, IconList } from "@tabler/icons-react"
import { format } from "timeago.js"

import TopicBadge from "@love-irish/resource-topic-badge"
import Title from "@love-irish/core-title"
import Content from "@love-irish/core-content"
import formatter from "design_system/utils/formatter"

import { cn } from "design_system/libs"

// Loaded before ./codex_record on purpose: codex_record pulls in resource-codex-meta-components, which
// circularly re-requires this barrel (for addDynamicTags). Requiring these leaf modules first means the
// barrel's re-exported bindings are already populated when that cycle re-enters (breaks jest.requireActual
// otherwise — the spread over a partially-loaded module dereferences an undefined submodule).
import { addDynamicTags } from "./shared"
import {
  ResourceActions,
  type ResourceActionsProps,
} from "./shared/resource_actions"
import CodexRecordListItem from "./codex_record"
import type { ResourceListItemDisplay } from "./display"
import InterviewListItem from "./interview"
import MiniGameListItem from "./mini_game"
import BookListItem from "./book"
import WebsiteListItem from "./website"
import AppListItem from "./app"
import DictionaryListItem from "./dictionary"
import Basic from "./basic"
import GuestbookListItem from "./guestbook"
import PodcastListItem from "./podcast"
import CommunityListItem from "./community"
import CourseListItem from "./course"
import MovieListItem from "./movie"
import TVShowListItem from "./tv_show"
import YouTubeChannelListItem from "./youtube_channel"
import RadioStationListItem from "./radio_station"
import MusicAlbumListItem from "./music_album"
import AudiobookListItem from "./audiobook"
import DocumentaryListItem from "./documentary"
import FlashcardSetListItem from "./flashcard_set"
import VideoGameListItem from "./video_game"
import BoardGameListItem from "./board_game"
import NewsSourceListItem from "./news_source"
import PublicationListItem from "./publication"
import ComicListItem from "./comic"
import EventListItem from "./event"
import CalendarEventListItem from "./calendar_event"
import TeacherListItem from "./teacher"
import ToolListItem from "./tool"
import MusicVideoListItem from "./music_video"
import SongListItem from "./song"

export const ResourceListItem = ({
  className,
  currentUser,
  item,
  collections = [],
  footer,
  display,
}: {
  className?: string
  currentUser?: unknown
  item: Record<string, unknown>
  collections?: unknown[]
  /** Custom footer content to replace the default "Added by..." text */
  footer?: React.ReactNode
  /** Chrome options (border, type label, read-more link), threaded from the list wrapper. */
  display?: ResourceListItemDisplay
}) => {
  const typename = item.type || item.__typename || item.typename

  switch (typename) {
    case "Item::CodexRecord":
    case "ItemCodexRecord":
      return (
        <CodexRecordListItem
          item={item}
          collections={collections}
          currentUser={currentUser}
          display={display}
        />
      )
    case "Item::Interview":
    case "ItemInterview":
      return (
        <InterviewListItem
          item={item}
          collections={collections}
          currentUser={currentUser}
        />
      )
    case "Item::Guestbook":
    case "ItemGuestbook":
      return (
        <GuestbookListItem
          item={item}
          collections={collections}
          currentUser={currentUser}
        />
      )
    case "Item::MiniGame":
    case "ItemMiniGame":
      return (
        <MiniGameListItem
          item={item}
          collections={collections}
          currentUser={currentUser}
        />
      )
    case "Item::Book":
    case "ItemBook":
      return (
        <BookListItem
          item={item}
          footer={footer}
          collections={collections}
          currentUser={currentUser}
        />
      )
    case "Item::Website":
    case "ItemWebsite":
      return (
        <WebsiteListItem item={item} className={className} footer={footer} />
      )
    case "Item::App":
    case "ItemApp":
      return <AppListItem item={item} className={className} footer={footer} />
    case "Item::Dictionary":
    case "ItemDictionary":
      return (
        <DictionaryListItem item={item} className={className} footer={footer} />
      )
    case "Item::Podcast":
    case "ItemPodcast":
      return (
        <PodcastListItem item={item} className={className} footer={footer} />
      )
    case "Item::Community":
    case "ItemCommunity":
      return (
        <CommunityListItem item={item} className={className} footer={footer} />
      )
    case "Item::Course":
    case "ItemCourse":
      return (
        <CourseListItem item={item} className={className} footer={footer} />
      )
    case "Item::Movie":
    case "ItemMovie":
      return <MovieListItem item={item} className={className} footer={footer} />
    case "Item::TVShow":
    case "ItemTVShow":
      return (
        <TVShowListItem item={item} className={className} footer={footer} />
      )
    case "Item::YouTubeChannel":
    case "ItemYouTubeChannel":
      return (
        <YouTubeChannelListItem
          item={item}
          className={className}
          footer={footer}
        />
      )
    case "Item::RadioStation":
    case "ItemRadioStation":
      return (
        <RadioStationListItem
          item={item}
          className={className}
          footer={footer}
        />
      )
    case "Item::MusicAlbum":
    case "ItemMusicAlbum":
      return (
        <MusicAlbumListItem item={item} className={className} footer={footer} />
      )
    case "Item::Audiobook":
    case "ItemAudiobook":
      return (
        <AudiobookListItem item={item} className={className} footer={footer} />
      )
    case "Item::Documentary":
    case "ItemDocumentary":
      return (
        <DocumentaryListItem
          item={item}
          className={className}
          footer={footer}
        />
      )
    case "Item::FlashcardSet":
    case "ItemFlashcardSet":
      return (
        <FlashcardSetListItem
          item={item}
          className={className}
          footer={footer}
        />
      )
    case "Item::VideoGame":
    case "ItemVideoGame":
      return (
        <VideoGameListItem item={item} className={className} footer={footer} />
      )
    case "Item::BoardGame":
    case "ItemBoardGame":
      return (
        <BoardGameListItem item={item} className={className} footer={footer} />
      )
    case "Item::NewsSource":
    case "ItemNewsSource":
      return (
        <NewsSourceListItem item={item} className={className} footer={footer} />
      )
    case "Item::Publication":
    case "ItemPublication":
      return (
        <PublicationListItem
          item={item}
          className={className}
          footer={footer}
        />
      )
    case "Item::Comic":
    case "ItemComic":
      return <ComicListItem item={item} className={className} footer={footer} />
    case "Item::Event":
    case "ItemEvent":
      return <EventListItem item={item} className={className} footer={footer} />
    case "Collection::CalendarEvent":
    case "CollectionCalendarEvent":
      return (
        <CalendarEventListItem
          item={item}
          className={className}
          footer={footer}
        />
      )
    case "Item::Teacher":
    case "ItemTeacher":
      return (
        <TeacherListItem item={item} className={className} footer={footer} />
      )
    case "Item::Tool":
    case "ItemTool":
      return <ToolListItem item={item} className={className} footer={footer} />
    case "Item::MusicVideo":
    case "ItemMusicVideo":
      return (
        <MusicVideoListItem item={item} className={className} footer={footer} />
      )
    case "Item::Song":
    case "ItemSong":
      return <SongListItem item={item} className={className} footer={footer} />
    default:
      return (
        <Basic
          item={item}
          currentUser={currentUser}
          className={className}
          footer={footer}
        />
      )
  }
}

// Named exports
export const CodexRecord = CodexRecordListItem
export { addDynamicTags }
export type { ResourceListItemDisplay } from "./display"
// Re-exported from the top-of-file imports (see the note there) so the cycle can't hit an unloaded module.
export { ResourceActions }
export type { ResourceActionsProps }

// Default export
export default ResourceListItem
